/** Small per-browser preferences: which columns a sheet shows, and anything
 *  else that is a matter of taste rather than a matter of record.
 *
 *  WHY NOT THE SERVER.  A dispatcher's column choice is not data about the
 *  organization, it does not need to survive a lost laptop, and putting it in
 *  Postgres means a migration, a route, and a write on every checkbox. The
 *  theme already works this way (`lib/theme.ts`) and this is the same kind of
 *  thing. The cost is honest and small: sign in on a different machine and you
 *  get the defaults.
 *
 *  EVERY ACCESS IS GUARDED.  Private windows, blocked site data and some
 *  embedded webviews throw on `localStorage` access rather than returning
 *  null -- not on read, on the property lookup itself. A dispatcher who cannot
 *  store a preference must still get a working screen, so every path here
 *  falls through to the caller's default.
 */

const NS = 'adv-crm.'

function read(key: string): string | null {
  try {
    return localStorage.getItem(NS + key)
  } catch {
    return null
  }
}

function write(key: string, value: string): void {
  try {
    localStorage.setItem(NS + key, value)
  } catch {
    // The preference applies for this session; it just will not be remembered.
    // Failing to persist must never mean failing to apply.
  }
}

/** Whatever survives of a stored array, as strings. `null` for anything that
 *  is not an array at all, so a caller can tell "absent" from "empty". */
function strings(value: unknown): string[] | null {
  if (!Array.isArray(value)) return null
  return value.filter((v): v is string => typeof v === 'string')
}

/** The `seen` list of a stored set: the catalogue it was last read against.
 *  `null` when there is no record, or it is the old bare-array shape. */
function storedSeen(key: string): string[] | null {
  const raw = read(key)
  if (!raw) return null
  try {
    const parsed: unknown = JSON.parse(raw)
    if (typeof parsed !== 'object' || parsed === null) return null
    return strings((parsed as { seen?: unknown }).seen)
  } catch {
    return null
  }
}

/** A remembered set of keys -- the visible columns of a sheet, say.
 *
 *  `allowed` IS THE CATALOGUE AND IT WINS. A stored list is filtered against
 *  it on the way out, so a column that has since been renamed or removed
 *  cannot resurrect itself. Without that, the first release that renames a
 *  column leaves people with a table missing a column and no way to tell why.
 *
 *  TWO THINGS USED TO BE THE SAME STATE, AND THEY ARE NOT. A key absent from a
 *  stored list can mean "switched off deliberately" or "did not exist when
 *  this was written", and the filter above treated both as off -- so a release
 *  that added columns delivered them to everybody EXCEPT the people who had
 *  ever opened the column picker, who got nothing new while losing whatever
 *  the same release had removed from the catalogue. Nothing on screen says
 *  why, and the fix a user would have to find is a menu they have already used
 *  once. So the catalogue the preference was last read against is stored
 *  beside it as `seen`, and a key that is in neither list is NEW rather than
 *  unwanted.
 *
 *  A NEW KEY ONLY SWITCHES ITSELF ON IF IT IS IN `fallback`. That is the line
 *  between the two rules: the sheet's own default set is what it shows to
 *  somebody with no preference at all, so a column added to it is one the
 *  sheet says everybody should see, while a new merely-available column stays
 *  available. And nothing already known is ever turned back on -- a column
 *  switched off stays off for as long as the catalogue still has it.
 *
 *  THE OLD BARE-ARRAY SHAPE STILL LOADS, and it keeps every choice it names.
 *  What it cannot do is say what it was written against, so it is read as
 *  having seen only the keys it lists: a default column somebody had switched
 *  off before this existed comes back, once. That is the one cost, it is
 *  visible, and it is one click to undo -- the other way round, three columns
 *  silently missing, is neither. An old list that is EMPTY is the exception
 *  and gains nothing; see the guard on the loop.
 *
 *  A STORED EMPTY LIST IS A CHOICE AND IS HONOURED -- see the comment on the
 *  filter itself. This paragraph used to say the opposite, which was true of
 *  an earlier version and was left behind when the code changed. It is the
 *  first thing a reader hits, and the sentence that would make sharing one
 *  preference key between two sheets look survivable.
 *
 *  THE ANSWER IS MEMBERSHIP, NOT ORDER. What comes back is in STORED order --
 *  whatever order the boxes happened to be ticked in -- so a caller that
 *  renders straight from it turns its own picker into a reorder control by
 *  accident: untick a column, tick it again, and it jumps to the end of the
 *  table. Ask this what is ON; take the order from your own catalogue.
 */
export function readSet(
  key: string,
  allowed: readonly string[],
  fallback: readonly string[],
): string[] {
  const raw = read(key)
  if (!raw) return [...fallback]
  let parsed: unknown
  try {
    parsed = JSON.parse(raw)
  } catch {
    return [...fallback]
  }

  let stored = strings(parsed)
  let seen: string[] | null = null
  if (stored === null) {
    if (typeof parsed !== 'object' || parsed === null) return [...fallback]
    stored = strings((parsed as { on?: unknown }).on)
    if (stored === null) return [...fallback]
    seen = strings((parsed as { seen?: unknown }).seen)
  }

  // A STORED EMPTY LIST IS A CHOICE AND IS HONOURED. This used to fall back
  // whenever the filtered result was empty, which quietly overrode somebody who
  // had switched every optional column off: their sheet came back with the six
  // defaults on the next load and nothing said why. The zero-column disaster
  // the fallback exists for cannot happen anyway — a caller marks its identity
  // column fixed. The fallback is for a preference that is ABSENT or corrupt,
  // not for one that is deliberately empty.
  const on = stored.filter((v) => allowed.includes(v))

  // AN OLD-SHAPE LIST THAT NAMES NOTHING HAS NOTHING TO UPGRADE, and this is
  // the one place the two rules above collide. An old list is read as having
  // seen only the keys it names; an EMPTY one names none, so every default
  // column would look new and the sheet somebody had deliberately stripped
  // bare would come back with the whole default set -- the exact override the
  // filter's own comment exists to prevent. There is no ambiguity to resolve
  // either: a list with nothing on it says "none of them" whatever the
  // catalogue held at the time. The new shape carries a real `seen` list, so
  // it is not guessing and the rule still applies to it.
  const known = seen ?? stored
  if (seen !== null || stored.length > 0) {
    for (const candidate of fallback) {
      // Not in the catalogue: a fallback naming a fixed or a removed column,
      // which is the caller's business and not a column anybody can tick.
      if (!allowed.includes(candidate)) continue
      if (known.includes(candidate) || on.includes(candidate)) continue
      on.push(candidate)
    }
  }

  // WRITTEN BACK ON READ, and that is not tidiness. `writeSet` is handed a
  // membership list and nothing else -- its only caller is `useColumns` in
  // lib/sheet.tsx -- so this is the one place that has the catalogue in its
  // hand and can record what a later release will have to compare against.
  // Skipping it would leave every browser permanently on the old shape, and
  // the next column added would be invisible all over again.
  write(key, JSON.stringify({ on, seen: [...allowed] }))
  return on
}

export function writeSet(key: string, values: readonly string[]): void {
  // THE `seen` LIST SURVIVES A TICK. It says which columns the user has been
  // offered, not which ones are on, so rewriting it from `values` would mark
  // every column they just switched OFF as never-seen -- and the next read
  // would helpfully switch the default ones back on again. `readSet` records
  // it on every mount, so it is there; the fall-back is for a caller that
  // writes a key it has never read, which `useColumns` never does.
  const seen = storedSeen(key) ?? [...values]
  write(key, JSON.stringify({ on: [...values], seen }))
}

/** A remembered SMALL SET of choices, stored bare and comma-joined -- the
 *  dispatch screen's lit tiles. Not `readSet`: that one carries a "seen" list
 *  that switches newly offered defaults back on, which is right for a sheet's
 *  columns and wrong for tiles somebody switched off on purpose. Anything not
 *  in `allowed` is dropped; nothing left is the fallback. */
export function readList(
  key: string,
  allowed: readonly string[],
  fallback: readonly string[],
): string[] {
  const raw = read(key)
  if (raw === null) return [...fallback]
  const on = raw.split(',').filter((v) => allowed.includes(v))
  return on.length ? on : [...fallback]
}

export function writeList(key: string, values: readonly string[]): void {
  write(key, values.join(','))
}

/** A remembered single choice -- which column a sheet is sorted on, say.
 *
 *  `allowed` WINS, as it does for `readSet`: a stored value the caller no
 *  longer recognises is the fallback, not an exception and not a blank sort.
 *  A value is stored bare, not as JSON, so there is nothing to fail to parse. */
export function readValue(
  key: string,
  allowed: readonly string[],
  fallback: string,
): string {
  const raw = read(key)
  return raw !== null && allowed.includes(raw) ? raw : fallback
}

export function writeValue(key: string, value: string): void {
  write(key, value)
}

/** A remembered size in pixels -- how wide somebody dragged a column.
 *
 *  `null` when nothing is stored, and ALSO when what is stored is not a number
 *  inside the bounds: a width from a screen this one is not, or a value
 *  somebody edited by hand, is the caller's default rather than a column
 *  drawn off the edge. Writing `null` forgets it. */
export function readNumber(key: string, min: number, max: number): number | null {
  const raw = read(key)
  if (raw === null || raw === '') return null
  const n = Number(raw)
  return Number.isFinite(n) && n >= min && n <= max ? n : null
}

export function writeNumber(key: string, value: number | null): void {
  write(key, value === null ? '' : String(Math.round(value)))
}
